import OcdpAPI from "../providers/OcdpAPI";

import classNames from "classnames/bind";
import { useEffect, useState } from "react";
import { Environment } from "../shared/core/Environment";
import { useDebouncedCallback } from "../shared/hooks/useDebouncedCallback";
import { Numbers } from "../shared/utils/Numbers";
import style from "../styles/pages/oraculo/oraculo.module.scss";

import { Button } from "../shared/components/Button";
import { Input } from "../shared/components/Input";
import { Loading } from "../shared/components/Loading";
import { Tabs } from "../shared/components/Tabs";
import { Title } from "../shared/components/Title";
import { If } from "../shared/components/control-statements/If";

import ROLES from "data/Roles";
import { useLocation, useNavigate, useParams } from "react-router";
import useEventListener from "shared/hooks/useEventListener";
import { UserStorage } from "shared/tools/UserStorage";
import { Empty } from "../shared/components/Empty";
import ContextSearch from "../shared/containers/oraculo/ContextSearch";
import Filters from "../shared/containers/oraculo/Filters";
import OracleDrawer from "../shared/containers/oraculo/OracleDrawer";
import UseAudience from "../shared/containers/oraculo/UseAudience";
import AudiencesDrawer from "../shared/containers/oraculo/audiences/AudiencesDrawer";
import { CustomerDrawer } from "../shared/containers/oraculo/customers/CustomerDrawer";
import {
	CUSTOMER_LIST_STYLE,
	CustomersTab,
} from "../shared/containers/oraculo/customers/CustomersTab";
import { IndexesTab } from "../shared/containers/oraculo/indexes/IndexesTab";
import { Oraculo } from "../shared/core/oraculo";

const css = classNames.bind(style);

const clone = (obj) => JSON.parse(JSON.stringify(obj));
const calcSelectedFilters = (filters) => {
	return Object.values(filters || {}).reduce((t, c) => t + c.length, 0);
};

const processFilterValue = (key, value) => {
	const _value = value;

	const bitwiseFilters = [
		"c.tag_period_day",
		"c.tag_period_week",
		"c.tag_period_year",
		"c.tag_temperature",
		"c.tag_climate",
	];

	if (key == "o.climate_temperature") {
		for (const idx in value) {
			switch (value[idx]) {
				case "COLD":
					_value[idx] = { operand: "lt", value: 10, alias: value[idx] };
					break;
				case "SOFT":
					_value[idx] = { operand: "bt", value: [10, 20], alias: value[idx] };
					break;
				case "HOT":
					_value[idx] = { operand: "bt", value: [20, 30], alias: value[idx] };
					break;
				case "VERY_HOT":
					_value[idx] = { operand: "gt", value: 30, alias: value[idx] };
					break;
			}
		}

		return _value;
	} else if (bitwiseFilters.includes(key)) {
		const bitwiseOperators = [];

		if (_value.includes("0")) {
			bitwiseOperators.push({ operand: "eq", value: 0 });
		}

		if (_value.some((value) => value != "0")) {
			const bitwiseValue = _value.reduce(
				(newValue, currentValue) => newValue | parseInt(currentValue)
			);

			bitwiseOperators.push({ operand: "bitwise_and", value: bitwiseValue });
		}

		return bitwiseOperators;
	} else {
		return { operand: "in", value: _value };
	}
};

export default function Database() {
	let userRole = UserStorage.getLoggedUser()?.role;

	const tabs = {
		CUSTOMERS: {
			enabled: true,
			key: "CUSTOMERS",
			name: "Clientes",
		},
		INDEXES: {
			enabled: [ROLES.SUPPORT, ROLES.ADMIN].includes(userRole),
			key: "INDEXES",
			name: "Índices",
		},
	};

	const [customers, setCustomers] = useState([]);
	const [selected_customer, setSelectedCustomer] = useState(null);
	const [subtitle, setSubtitle] = useState("");
	const [active_tab, setActiveTab] = useState(tabs.CUSTOMERS.key);
	const [open_oracle, setOpenOracle] = useState(false);

	const [open_use_audience, setOpenUseAudience] = useState(false);
	const [customerStyle, setCustomerStyle] = useState(CUSTOMER_LIST_STYLE.grid);
	const [selected_filters, setSelectedFilters] = useState({});
	const [open_filters_apply, setOpenFiltersApply] = useState(false);
	const [applied_filters, setAppliedFilters] = useState(false);
	const [stats, setStats] = useState(null);
	const [loading, setLoading] = useState(true);
	const [in_progress, setInProgress] = useState(false);
	const [selected_audience, setSelectedAudience] = useState(null);
	const [audience_to_edit, setAudienceToEdit] = useState(null);
	const [search, setSearch] = useState("");
	const [occurrences, setOcurrences] = useState(null);
	const [open_audiences, setOpenAudiences] = useState(false);
	const [configurations, setConfigurations] = useState({});
	const [audienceUsed, setAudienceUsed] = useState(false);
	const [openContextSearch, setOpenContextSearch] = useState(false);
	const [environment_name, setEnvironmentName] = useState("");
	const [context, setContext] = useState(null);
	const [boxes, setBoxes] = useState([]);
	const [links, setLinks] = useState([]);
	const [loadingAudience, setLoadingAudience] = useState(false);
	const [page_infos, setPageInfos] = useState({
		page: null,
		execution_id: null,
		has_next: false,
		force: false,
		sort: null,
		limit: null,
	});

	const routeParams = useParams();
	const location = useLocation();
	const navigate = useNavigate();

	const brandId = () => context?.brand?.id || null;
	const unitId = () => context?.unit?.id || null;

	useEffect(() => {
		const getAudience = async () => {
			setLoadingAudience(true);
			const ocdp_api = new OcdpAPI();
			const audiences = await ocdp_api.audiences();

			const selectedAudience = audiences.find(
				(a) => String(a.id) === routeParams.id
			);

			if (!selectedAudience) {
				return
			}

			selectedAudience.brand = selectedAudience.brand_id ? { id: selectedAudience.brand_id } : null;
			selectedAudience.unit = selectedAudience.unit_id ? { id: selectedAudience.unit_id } : null;

			installAudience(selectedAudience);

			if (!isSuggestedAudience(selectedAudience)) setAudienceUsed(true);
		};

		if (routeParams?.id) getAudience();
	}, [routeParams]);

	const updatePageInfos = (infos, ignore_previous = false) => {
		const defaults = {
			page: 0,
			execution_id: null,
			has_next: false,
			force: false,
			sort: null,
			limit: null,
		};

		setPageInfos((old) => {
			if (ignore_previous) {
				return Object.assign(defaults, infos);
			}

			const _old = Object.assign(defaults, old);
			return Object.assign(_old, infos);
		});
		setLoadingAudience(false);
	};

	const updateContext = ({ type, brand, unit, multi_brand }) => {
		setContext({
			type,
			brand,
			unit,
			multi_brand
		});

		setLoading(true);

		putSelectedFilters(null);
		setAppliedFilters(null);
		setAudienceToEdit(null);
		setSelectedAudience(null);
		setBoxes([]);
		setLinks([]);
		setCustomers([]);
		setStats({});
		setSubtitle("");

		execute({
			brand_id: brand?.id,
			unit_id: unit?.id,
			boxes: [],
			links: [],
			schema: [],
			filters: null,
			sort: null,
			limit: null,
			force_context: true
		});
	}

	const isPhoneNumber = (string) => {
		const regex = /^[\d(]/;
    return regex.test(string);
	}

	const fetchData = async () => {
		if (page_infos.execution_id == null) {
			setCustomers([]);
			updatePageInfos({}, true);
			return;
		}

		const filters = clone(applied_filters || {});
		const { page, execution_id } = page_infos;

		const params = {
			brand_id: brandId(),
			unit_id: unitId(),
			filters: filters,
			page: page, // Se o limite foi definido, sempre retornar a página zero
			size: 201, // Foi escolhido 201 porque é multiplo de 3
		};

		params.term = search;
		if (isPhoneNumber(search) || (/^\d+$/.test(search) && search.length > 7)) {
			params.term = Numbers.onlyDigits(search);
		}


		const ocdp_api = new OcdpAPI();
		const customers_page = await ocdp_api.fetch(execution_id, params);

		const { page_items, page_size, page_number, term_occurrences } =
			customers_page;

		setCustomers([...customers, ...page_items]);

		const infos = {
			execution_id: execution_id,
			has_next: page_items.length == page_size,
			page: +page_number + 1,
			force: false,
		};
		updatePageInfos(infos);
		setOcurrences(search ? term_occurrences || 0 : null);
	};

	const fetchStats = async () => {
		if (loadingAudience) return

		const { execution_id } = page_infos;

		const ocdp_api = new OcdpAPI();

		const filters = clone(applied_filters || {});
		const response = await ocdp_api.stats(execution_id, {
			brand_id: brandId(),
			unit_id: unitId(),
			filters,
		});

		const { stats } = response;

		const str = stats?.customers == 1 ? "cliente" : "clientes";
		setSubtitle(Numbers.formatInt(stats?.customers) + ` ${str}`);
		setStats(stats || {});
	};

	const handleOnQuery = async ({
		boxes,
		links,
		execution_id,
		filters = null,
		sort = null,
		limit = null,
	}) => {
		setBoxes(boxes);
		setLinks(links);
		setCustomers([]);
		setStats({});
		setSubtitle("");
		setAppliedFilters(filters || null);

		const infos = {
			execution_id: execution_id,
			has_next: true,
			page: 0,
			force: true,
			sort: sort,
			limit: limit,
		};
		updatePageInfos(infos, true);

		setOpenOracle(false);
	};

	const execute = async ({ brand_id, unit_id, boxes, links, schema, sort, limit, filters, force_context = false }) => {
		try {
			setInProgress(true);
			setLoading(true);

			brand_id = force_context ? brand_id || null : brand_id || brandId();
			unit_id = force_context ? unit_id || null : unit_id || unitId();

			const { execution_id, status } =
				(await waitQueryExecution(schema, { brand_id: brand_id, unit_id: unit_id, sort, limit })) || {};
			if (!execution_id || status == "ERROR") {
				setInProgress(false);
				return alert({
					text: "Ocorreu um erro durante a execução da consulta",
				});
			}
			handleOnQuery({
				boxes,
				links,
				execution_id,
				status,
				filters,
				sort,
				limit,
			});
			setLoadingAudience(false);
		} catch (err) {
			console.error(err);
			setInProgress(false);
		}
	};

	const applyFilterAndFetchData = () => {
		let { execution_id } = page_infos;

		const processed_filters = {};
		Object.entries(clone(selected_filters || {})).forEach(([key, value]) => {
			if (value) {
				processed_filters[key] = processFilterValue(key, value);
			}
		});

		setAppliedFilters(processed_filters);
		setCustomers([]);
		setLoading(true);

		/**
		 * Se for um público estático selecionado e o público e for original
		 * a campanha deverá se reexecutada, resetando a lista de clientes da fotografia
		 */
		if (
			selected_audience?.type == "STATIC" &&
			execution_id.indexOf("audience-") > -1
		) {
			const query = waitQueryExecution(boxes.map((n) => n.data));
			execution_id = query.execution_id;
		}

		const infos = {
			execution_id: execution_id,
			has_next: true,
			page: 0,
			force: true,
		};
		updatePageInfos(infos);
	};

	const reset = () => {
		navigate('/base-de-dados')
		const configs = Environment.configurations;

		if (brandId() || unitId()) {
			updateContext({ ...context });
		} else if (configs.ocdp_execution_id) {
			setBoxes([]);
			setLinks([]);
			putSelectedFilters(null);
			setAppliedFilters(null);
			setAudienceToEdit(null);
			setSelectedAudience(null);
			setCustomers([]);
			setLoading(true);

			const infos = {
				execution_id: configs.ocdp_execution_id,
				has_next: true,
				page: 0,
				force: true,
			};

			updatePageInfos(infos, true);
		}
	};

	const isSuggestedAudience = (audience) => {
		return audience?.name?.trimStart().startsWith("[Olga] ");
	}

	const installAudience = (audience) => {
		const { id, brand, unit, boxes, schema, links, filters, execution_id, sort, limit } =
			audience;

		if (location.pathname !== `/base-de-dados/${id}`) {
			navigate(`/base-de-dados/${id}`)
		}

		if (!isSuggestedAudience(audience)) setSelectedAudience(audience);

		setCustomers([]);
		setContext({ brand, unit });

		const _selected_filters = {};
		for (const [key, filter] of Object.entries(filters || {})) {
			if (Array.isArray(filter)) {
				_selected_filters[key] = filter.map((v) => v.alias);
			} else {
				_selected_filters[key] = filter.value;
			}
		}

		setSelectedFilters(_selected_filters);
		setOpenFiltersApply(false);

		if (audience.type == "STATIC") {
			setBoxes(boxes || []);
			setLinks(links || []);
			setAppliedFilters(filters || null);
			const infos = {
				execution_id: execution_id,
				has_next: true,
				page: 0,
				force: true,
				sort: sort,
				limit: limit,
			};
			updatePageInfos(infos, true);
		} else {
			execute({
				brand_id: brand?.id,
				unit_id: unit?.id,
				boxes: boxes || [],
				links: links || [],
				schema: schema || [],
				filters: filters || null,
				sort: sort || null,
				limit: limit || null,
				force_context: true
			});
		}
	};

	const putSelectedFilters = (filters) => {
		setSelectedFilters(filters);
		setOpenFiltersApply(calcSelectedFilters(filters) > 0);
	};

	const debouncedSearch = useDebouncedCallback((nextValue) => {
		setCustomers([]);

		const infos = {
			execution_id: page_infos.execution_id,
			page: 0,
			force: true,
			has_next: true,
		};
		updatePageInfos(infos);
	}, 1000);

	useEffect(() => {
		if (page_infos.force) {
			setLoading(true);

			const proms = [fetchData(), fetchStats()];
			Promise
				.all(proms)
				.then(() => {
					setInProgress(false);

					setTimeout(() => {
						if (!loadingAudience && !OcdpAPI.isRequestInProgress("fetch")) setLoading(false);
					}, 0);
				});
		}
	}, [page_infos]);

	useEffect(() => {

		if (!Environment.name) {
			const install = async () => {
				await Environment.install();
				setEnvironmentName(Environment.name);
			}
			install();
		} else {
			setEnvironmentName(Environment.name);
		}

		// Primeiro carregamento
		const configs = Environment.configurations;

		if (!configs) {
			setSubtitle("0 clientes");
			setLoading(false);
			return
		}

		if (Object.keys(configs).length === 0) {
			const installEnvironment = async () => {
				setLoading(true);

				await Environment.installConfigurations();
				setConfigurations(Environment.configurations);

				setLoading(false);
			}

			installEnvironment();
		}

		if (configs.ocdp_execution_id) {
			const infos = {
				execution_id: configs.ocdp_execution_id,
				has_next: true,
				page: 0,
				force: true,
			};
			updatePageInfos(infos, true);
		}
	}, []);

	useEffect(() => {
		if (configurations.ocdp_execution_id) {
			const infos = {
				execution_id: configurations.ocdp_execution_id,
				has_next: true,
				page: 0,
				force: true,
			};
			updatePageInfos(infos, true);
		}
	}, [configurations]);

	const getContextSubtitle = () => {
		const pieces = [environment_name];

		if (context?.multi_brand && context?.brand && context?.brand.name) {
			pieces.push(context.brand.name);
		}

		if (context?.unit && context?.unit.name) {
			pieces.push(context.unit.name);
		}

		return pieces.join(' / ');
	};

	const [username, setUsername] = useState(null);

	useEffect(() => {
		setUsername(UserStorage.getLoggedUser()?.username);
	}, []);

	useEventListener("ON_LOGIN", () => {
		setUsername(UserStorage.getLoggedUser()?.username);
	});

	return (
		<>
			<Title title="Base de Dados" subtitle={getContextSubtitle()}>
				<If cond={selected_audience}>
					<div
						className={style.closeAudience}
						onClick={() => {
							setSelectedAudience(null);
							setAudienceUsed(false);
							reset();
						}}
					>
						Fechar este público <i className="fas fa-times" />
					</div>
				</If>
				<If cond={!selected_audience && (applied_filters || boxes?.length > 0 || page_infos?.limit !== null || page_infos?.sort !== null)}>
					<div className={style.closeAudience} onClick={() => reset()}>
						Remover filtros <i className="fas fa-times" />
					</div>
				</If>

				<If cond={active_tab === tabs.CUSTOMERS.key}>
					<Input
						search
						value={search}
						records={
							occurrences !== null && Numbers.formatInt(occurrences || 0)
						}
						placeholder="Buscar por..."
						className="mb-0 pt-0"
						design="search"
						prefix
						prefixWidth={20}
						onChange={(t) => {
							setSearch(t.target.value);
							debouncedSearch(t.target.value);
						}}
					/>
					<ToggleListStyle
						selectedStyle={customerStyle}
						onChange={(s) => setCustomerStyle(s)}
					/>
				</If>

				{([ROLES.SUPPORT, ROLES.ADMIN, ROLES.MARKETING, ROLES.SAC].includes(userRole)) && (
					<>
						<Button
							design="two"
							className="ml-15"
							onClick={() => setOpenAudiences(true)}
						>
							<i className="fal fa-users" />
						</Button>

						<Button
							design="two"
							className="ml-15"
							onClick={() => setOpenContextSearch(true)}
						>
							<i className="fal fa-rotate" />
						</Button>

						<If cond={applied_filters || boxes?.length > 0 || page_infos?.limit !== null || page_infos?.sort !== null}>
							<Button
								design="primary"
								text={"Usar público"}
								onClick={() => setOpenUseAudience(true)}
								className="ml-15"
								disabled={loading}
							/>
						</If>
						<If cond={!applied_filters && boxes.length == 0 && page_infos?.limit === null && page_infos?.sort === null}>
							<Button
								onClick={() => setOpenOracle(true)}
								design="primary"
								className="ml-15"
							>
								Filtrar
							</Button>
						</If>
					</>
				)}
			</Title>

			<div className={style.tabsContainer}>
				<Tabs
					activeTab={active_tab}
					onTabSelected={(tab) => setActiveTab(tab)}
					tabs={Object.values(tabs).filter(t => t.enabled)}
				/>

				{!loading && <span className={style.customerQuantity}>{subtitle}</span>}
			</div>
			<If cond={(applied_filters || boxes.length > 0) && !loading}>
				<Filters
					boxes={boxes}
					filters={applied_filters}
					editFilters={() => setOpenOracle(true)}
					audienceUsed={audienceUsed}
					selectedAudience={selected_audience}
				/>
			</If>

			<If cond={active_tab === tabs.CUSTOMERS.key}>
				<If cond={customers.length > 0 && !loading}>
					<CustomersTab
						style={customerStyle}
						customers={customers}
						brandId={brandId()}
						unitId={unitId()}
						onLoadMoreData={fetchData}
						onSelectCustomer={(customer_id) => setSelectedCustomer(customer_id)}
					/>
				</If>
				<If cond={loading}>
					<div className={style.loading}>
						<div className="w-100 d-flex align-center justify-center flex-1 pt-25 pb-25">
							<Loading />
						</div>
					</div>
				</If>
				<If cond={customers.length == 0}>
					<If cond={!loading}>
						<Empty>
							<i className="far fa-database" />
							<p>Nenhum cliente encontrado.</p>
						</Empty>
					</If>
				</If>
			</If>

			<If cond={active_tab === tabs?.INDEXES?.key}>
				<If cond={loading}>
					<div className={style.loading}>
						<div className="w-100 d-flex align-center justify-center flex-1 pt-25 pb-25">
							<Loading />
						</div>
					</div>
				</If>
				<If cond={!loading}>
					<IndexesTab
						stats={stats}
						selected_filters={selected_filters}
						onSelectFilters={(filters) => putSelectedFilters(filters)}
					/>
				</If>
			</If>

			<OracleDrawer
				visible={open_oracle}
				onQuery={execute}
				boxes={boxes}
				links={links}
				sort={page_infos?.sort}
				limit={page_infos?.limit}
				disabledVariables={
					unitId() ? (
						['o.unit_id', 'o.brand_id']
					) : (
						brandId() ? (
							['o.unit_id']
						) : (
							[]
						)
					)}
				onClose={() => {
					setOpenOracle(false);
					setSelectedAudience(null);
					setAudienceToEdit(null);
				}}
				audience={audience_to_edit}
				in_progress={in_progress}
			/>

			<UseAudience
				visible={open_use_audience}
				execution_id={page_infos?.execution_id}
				audience={selected_audience}
				onSave={(audience) => {
					installAudience(audience);
				}}
				state={{
					brand_id: selected_audience ? selected_audience?.brand?.id : brandId(),
					unit_id: selected_audience ? selected_audience.unit?.id : unitId(),
					schema: boxes.map((b) => b.data),
					boxes: boxes,
					links: links,
					filters: applied_filters,
					sort: page_infos?.sort,
					limit: page_infos?.limit,
				}}
				onClose={() => setOpenUseAudience(false)}
			/>

			<CustomerDrawer
				visible={selected_customer}
				customerId={selected_customer}
				brandId={brandId()}
				unitId={unitId()}
				onClose={() => setSelectedCustomer(null)}
			/>

			<ContextSearch
				visible={openContextSearch}
				onChangeContext={(context) => {
					updateContext(context);
					setOpenContextSearch(false);
				}}
				onClose={() => setOpenContextSearch(false)}
			/>

			<AudiencesDrawer
				visible={open_audiences}
				onClose={() => setOpenAudiences(false)}
				openAudience={(audience) => {
					installAudience(audience);
					setOpenAudiences(false);
					if (!isSuggestedAudience(audience)) setAudienceUsed(true);
				}}
				onEditAudience={(audience) => {
					setAudienceToEdit(audience);
					setOpenOracle(true);
				}}
				selectedAudience={selected_audience}
			/>

			<If cond={open_filters_apply}>
				<div className={style.filtersApply}>
					<div className="d-flex align-baseline">
						<div
							className={style.clear}
							onClick={() => putSelectedFilters(null)}
						>
							<i className="fas fa-times" />
						</div>
						<p>{calcSelectedFilters(selected_filters)} selecionado(s)</p>
					</div>
					<div
						className={style.add}
						onClick={() => {
							applyFilterAndFetchData();
							setOpenFiltersApply(false);
							setActiveTab(tabs.CUSTOMERS.key);
						}}
					>
						Adicionar ao filtro
					</div>
				</div>
			</If>
		</>
	);
}

const ToggleListStyle = ({ selectedStyle, onChange }) => {
	return (
		<div className={style.toggle}>
			<div
				className={css({
					side: true,
					active: selectedStyle === CUSTOMER_LIST_STYLE.grid,
				})}
				onClick={() => onChange(CUSTOMER_LIST_STYLE.grid)}
			>
				<i className="fal fa-grid" />
			</div>
			<div
				className={css({
					side: true,
					active: selectedStyle === CUSTOMER_LIST_STYLE.table,
				})}
				onClick={() => onChange(CUSTOMER_LIST_STYLE.table)}
			>
				<i className="fal fa-list" />
			</div>
		</div>
	);
};

const waitQueryExecution = async (schema, options) => {
	const ocdp_api = new OcdpAPI();

	const { execution_id } = await ocdp_api.query(schema, options);
	if (!execution_id) {
		return null;
	}

	return await Oraculo.waitQueryExecution(execution_id);
};
